// 📦 React & Related
import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// 🧩 Third-Party Libraries
import { Table } from 'antd';

// 🛠️ Utilities & Constants
import Helpers from '@Apis/helpers';
import { DEFAULT_CUR_ROUND_OFF, QUANTITY, toISTDate } from '@Apis/constants';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';

// 🎯 Redux Actions
import ProductActions from '@Actions/productActions';

// 🧱 UI Components
import H3Text from '@Uilib/h3Text';
import PRZModal from '../../../../Common/UI/PRZModal';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import RecentTransactions from '@Components/Inventory/ManageProducts/ViewProduct/RecentTransactions';

// 🎨 Styles
import './style.scss';


function ViewAccountPayableInvoiceLines({ apInvoiceLines, selectedAPI, visibleColumns, cfApInvoiceLine, priceMasking, user, MONEY, isVendorOverseas,
  }) {
  const [state, setState] = useState({
    showPRZModal: false,
    selectedDocumentId: null,
  });
  const {
    showPRZModal,
    selectedDocumentId,
  } = state;
  const updateState = (object) => {
    setState({
      ...state,
      ...object,
    });
  };

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const getColumns = () => {

    const columns = [
      {
        title: '',
        responsive: ['xs'],
        render: (record) => (
          <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
            <div className="mobile-list__item">
              <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
                <H3Text text={`SKU Number: ${record?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
              </Link>
              <H3Text text={record?.product_sku_info?.product_sku_name} className="mobile-list__item-info" />
              <H3Text text={`Quantity: ${QUANTITY(record?.quantity, record?.uom_info?.[0]?.precision)} ${record?.uom_info?.[0]?.qc?.toProperCase()}`} className="mobile-list__item-info" />
              <H3Text text={`Required By Date: ${toISTDate(record?.required_by_date, 'DD/MM/YYYY')}`} className="mobile-list__item-info" />
              <H3Text text={`Planning Number: ${record?.planning_number}`} className="mobile-list__item-info" />
            </div>
          </Link>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Product',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => {
          return (
            <Fragment>
              <div onClick={() => updateState({ showPRZModal: true, selectedDocumentId: record?.product_sku_info?.product_sku_id })}>
                <a>
                  {`#${record?.product_sku_info?.internal_sku_code}`}
                </a>
              </div>
              <div className="flex-display" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ maxWidth: '200px' }}>
                  {`${record.product_sku_info?.ref_product_code ? `${record.product_sku_info?.ref_product_code} - ` : ''}${record?.product_sku_info?.product_sku_name}`?.trim()}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: (isDataMaskingPolicyEnable && isHideCostPrice) ? '' : record?.remarks,
                    }}
                    className="table-subscript"
                    style={{
                      width: '200px',
                      whiteSpace: 'pre-wrap',
                    }}
                  />
                  {(isDataMaskingPolicyEnable && isHideCostPrice) && <HideValue showPopOver />}
                  {!record?.tenant_product_id && <H3Text text="New Product" className="view-pi-selection__new-product" />}
                </div>
                <div className="flex-align-center-justify-between" style={{ gap: '10px' }}>
                  {record?.product_sku_info?.product_category_info?.category_path?.length > 0 && (
                    <ProductCategoryLabel
                      categoryPath={record?.product_sku_info?.product_category_info?.category_path}
                      categoryName={record?.product_sku_info?.product_category_info?.category_path?.at(
                        -1,
                      )}
                      containerStyle={{
                        width: 'fit-content',
                      }}
                    />
                  )}
                  {Helpers.getPermission(Helpers.permissionEntities.PURCHASE_INDENT, Helpers.permissionTypes.CREATE, user) && (
                    <RecentTransactions
                      tenantId={selectedAPI?.tenant_id}
                      productSkuId={record?.product_sku_info?.product_sku_id}
                      internalSkuCode={record?.product_sku_info?.internal_sku_code}
                      refProductCode={record?.product_sku_info?.ref_product_code}
                      productSkuName={record?.product_sku_info?.product_sku_name}
                    />
                  )}
                </div>
              </div>
            </Fragment>
          );
        },
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Quantity',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => `${QUANTITY(item?.quantity, item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`,
        visible: visibleColumns?.QUANTITY?.visible,
      },
      {
        title: 'Unit Price',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (
          <div>
            {(isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view unit price"} /> : MONEY((item?.offer_price), selectedAPI?.org_currency_info?.currency_code)}
           {!isVendorOverseas && ( <H3Text
              text={`+ tax@${item?.tax_info?.tax_value}%`}
              className="danger-subtext"
            />)}
          </div>
        ),
        visible: visibleColumns?.UNIT_PRICE?.visible,
      },
      {
        title: 'Discount',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <Fragment>
            {
              Number(record?.line_discount_amount) > 0
                ? (
                  <Fragment>
                    {(record?.is_discount_in_percent ? `${Number(record?.line_discount_percentage).toPrecision(DEFAULT_CUR_ROUND_OFF) || '0'}%` : MONEY(record?.line_discount_amount, selectedAPI?.org_currency_info?.currency_code))}
                  </Fragment>
                )
                : '-'
            }
          </Fragment>
        ),
        visible: visibleColumns?.DISCOUNT?.visible,
        className: !selectedAPI?.is_line_wise_discount ? 'display-none' : '',
      },
      {
        title: 'Total',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view unit price"} /> : MONEY((((Number(item.quantity || 1) * Number(item.offer_price || 1)) * (item.line_discount_percentage ? Number(100 - item.line_discount_percentage) / 100 : 1)) * (1 + Number(item?.tax_info?.tax_value) / 100)), selectedAPI?.org_currency_info?.currency_code),
        visible: visibleColumns?.TOTAL?.visible,
      },
    ];
    if (cfApInvoiceLine?.length) {
      columns.splice(4, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfApInvoiceLine, visibleColumns, null));
    };

    return columns;
  }

  const getData = (data) => (JSON.parse(JSON.stringify(data)));
  return (
    <Fragment>
      <div className='pi-table-wrapper'>
        <Table
          bordered
          showHeader
          title={() => (`${apInvoiceLines?.length || 0} AP Invoice Lines `)}
          size='small'
          columns={getColumns()}
          dataSource={getData(apInvoiceLines || [])}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          updateState({ showPRZModal: false, selectedDocumentId: null });
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, ProductReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  selectedOrgProduct: ProductReducers.selectedOrgProduct,
  getOrgProductByIdLoading: ProductReducers.getOrgProductByIdLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getOrgProductById: (orgId, tenantId, productSkuId, callback) => dispatch(ProductActions.getOrgProductById(orgId, tenantId, productSkuId, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewAccountPayableInvoiceLines));